import React, { useEffect, useMemo, useState } from 'react'
import { Layout, Modal, InlineAlert } from '@design-systems/canvas-ui'
import { useQuery } from '@apollo/client/react'
import {
  GET_ACTIVE_BANNERS,
  Banner,
} from 'graphql/queries/banner/bannerQueries'
import {
  convertTextToLinks,
  SYSTEM_EVENTS_BANNER,
  SYSTEM_EVENTS_SPLASH_PAGE,
} from './bannerUtils'
import { useAuth } from '@praxis/component-auth'

const DISMISSED_BANNERS_STORAGE_KEY = 'plm-dismissed-banner-ids'

const getDismissedBannerIds = (): string[] => {
  try {
    const storedValue = window.localStorage.getItem(
      DISMISSED_BANNERS_STORAGE_KEY,
    )
    if (!storedValue) return []

    const parsedValue: unknown = JSON.parse(storedValue)
    if (
      !Array.isArray(parsedValue) ||
      !parsedValue.every((value) => typeof value === 'string')
    ) {
      window.localStorage.removeItem(DISMISSED_BANNERS_STORAGE_KEY)
      return []
    }

    return parsedValue
  } catch {
    return []
  }
}

const saveDismissedBannerIds = (bannerIds: string[]) => {
  try {
    if (bannerIds.length === 0) {
      window.localStorage.removeItem(DISMISSED_BANNERS_STORAGE_KEY)
    } else {
      window.localStorage.setItem(
        DISMISSED_BANNERS_STORAGE_KEY,
        JSON.stringify(bannerIds),
      )
    }
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
}

const getBannerVariant = ({
  banner,
  splashPageActive,
}: {
  banner?: Banner
  splashPageActive: boolean
}) => {
  const style = banner?.metadata?.styles?.[0]?.value
  // todo account for fake production mode, when we develop it.
  if (splashPageActive) {
    return 'caution'
  }
  if (style === 'error') {
    return 'error'
  } else if (style === 'info' || style === 'default') {
    return 'info'
  } else if (style === 'success') {
    return 'success'
  } else if (style === 'alert') {
    return 'caution'
  }
}

const MoreInfoModal = ({
  banners,
  isOpen,
  onClose,
  onDismiss,
}: {
  banners: Banner[]
  isOpen: boolean
  onClose: () => void
  onDismiss: (bannerId: string) => void
}) => {
  return (
    <Modal headingText="All banners" isOpen={isOpen} onClose={onClose}>
      {banners.map((banner) => (
        <InlineAlert
          key={banner?.id}
          heading={banner?.header}
          variant={getBannerVariant({ banner, splashPageActive: false })}
          onClose={() => onDismiss(banner.id)}
          className="hc-mb-2x"
        >
          {convertTextToLinks(banner?.message)}
        </InlineAlert>
      ))}
    </Modal>
  )
}

const AppBanner = () => {
  const [showMoreInfo, setShowMoreInfo] = useState(false)
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>(
    getDismissedBannerIds,
  )
  const { isAuthenticated } = useAuth()

  const isLoggedIn = isAuthenticated()
  const { data: activeBanners, startPolling } = useQuery(GET_ACTIVE_BANNERS, {
    skip: !isLoggedIn,
  })
  const getActiveBanners = useMemo(
    () => activeBanners?.getActiveBanners || [],
    [activeBanners],
  )
  const activeBannersData = useMemo(
    () =>
      getActiveBanners.filter(
        (banner: Banner) => banner?.level?.description === SYSTEM_EVENTS_BANNER,
      ),
    [getActiveBanners],
  )
  const activeSplashPageData = useMemo(
    () =>
      getActiveBanners.filter(
        (banner: Banner) =>
          banner?.level?.description === SYSTEM_EVENTS_SPLASH_PAGE,
      ),
    [getActiveBanners],
  )
  const visibleActiveBanners = useMemo(
    () =>
      activeBannersData.filter(
        (banner) => !dismissedBannerIds.includes(banner.id),
      ),
    [activeBannersData, dismissedBannerIds],
  )
  const moreThanOneBanner = visibleActiveBanners.length > 1
  const splashPageActive =
    activeBannersData?.length !== 0 && activeSplashPageData?.length !== 0

  const displayBanner = useMemo(() => {
    if (splashPageActive) {
      return {
        header: 'PLM is currently locked and unavailable to users.',
      } as Banner
    } else if (visibleActiveBanners.length > 0) {
      return visibleActiveBanners[0]
      // todo account for fake production mode, when we develop it.
    }
    // } else if (apiConfig.fakeProduction) {
    //   return { header: "You're in pretend production mode" }
    // }
  }, [splashPageActive, visibleActiveBanners])
  const bannerVariant = useMemo(
    () => getBannerVariant({ banner: displayBanner, splashPageActive }),
    [displayBanner, splashPageActive],
  )

  useEffect(() => {
    if (isLoggedIn) {
      startPolling(60000)
    }
  }, [isLoggedIn, startPolling])

  useEffect(() => {
    if (!activeBanners?.getActiveBanners) return

    const activeBannerIds = new Set(
      activeBannersData.map((banner) => banner.id),
    )
    setDismissedBannerIds((currentIds) => {
      const reconciledIds = currentIds.filter((id) => activeBannerIds.has(id))
      saveDismissedBannerIds(reconciledIds)
      return reconciledIds
    })
  }, [activeBanners, activeBannersData])

  const dismissBanner = (bannerId: string) => {
    setDismissedBannerIds((currentIds) => {
      if (currentIds.includes(bannerId)) return currentIds

      const nextIds = [...currentIds, bannerId]
      saveDismissedBannerIds(nextIds)
      return nextIds
    })
  }

  const hasActiveBanners =
    getActiveBanners?.length !== 0 &&
    visibleActiveBanners.length > 0 &&
    activeSplashPageData?.length < 1
  const shouldRenderBanner = useMemo(() => {
    // add fake production mode to the condition when we support it.
    return hasActiveBanners || splashPageActive
  }, [hasActiveBanners, splashPageActive])

  return (
    <>
      {shouldRenderBanner && (
        <Layout.Banner
          variant={bannerVariant}
          actions={[
            {
              text: `View more ${moreThanOneBanner ? `(${visibleActiveBanners.length})` : ''}`,
              onClick: () => {
                setShowMoreInfo(true)
              },
            },
            ...(displayBanner?.id
              ? [
                  {
                    text: 'Dismiss',
                    onClick: () => dismissBanner(displayBanner.id),
                  },
                ]
              : []),
          ]}
        >
          {displayBanner?.header}
        </Layout.Banner>
      )}
      <MoreInfoModal
        banners={visibleActiveBanners}
        isOpen={showMoreInfo}
        onClose={() => setShowMoreInfo(false)}
        onDismiss={dismissBanner}
      />
    </>
  )
}

export default AppBanner
